const LISSEN_PROD_CDN_PREFIX = "https://cdn.lissenprod.lissen.live/";
// Exported so callers that need to *construct* a gs:// URL from scratch
// (rather than just rewriting an existing one) -- e.g.
// apps/api/services/catalog/on-demand-audio.ts's deterministic on-demand-
// publish paths -- have one shared source for the bucket name instead of
// hardcoding it again.
export const LISSEN_PROD_BUCKET_PREFIX = "gs://lissenprod.appspot.com/";

export function toLissenMediaStorageUrl<T extends string | null | undefined>(
  value: T,
): T {
  if (typeof value !== "string" || !value.startsWith(LISSEN_PROD_CDN_PREFIX)) {
    return value;
  }

  return `${LISSEN_PROD_BUCKET_PREFIX}${value.slice(
    LISSEN_PROD_CDN_PREFIX.length,
  )}` as T;
}

export function toLissenMediaStorageUrls<T extends string[] | null | undefined>(
  values: T,
): T {
  if (!values) {
    return values;
  }

  return values.map(toLissenMediaStorageUrl) as T;
}

export function toLissenMediaCdnUrl<T extends string | null | undefined>(
  value: T,
): T {
  if (
    typeof value !== "string" ||
    !value.startsWith(LISSEN_PROD_BUCKET_PREFIX)
  ) {
    return value;
  }

  return `${LISSEN_PROD_CDN_PREFIX}${value.slice(
    LISSEN_PROD_BUCKET_PREFIX.length,
  )}` as T;
}

export function toLissenMediaCdnUrls<T extends string[] | null | undefined>(
  values: T,
): T {
  if (!values) {
    return values;
  }

  return values.map(toLissenMediaCdnUrl) as T;
}

/**
 * The image id an imported page's avatar is uploaded under when it stands in
 * as an artist's picture (apps/api pageProfile.ts): a catalog picture of the
 * artist replaces it, as it would no picture.
 */
export const PAGE_AVATAR_IMAGE_ID_PREFIX = "page-avatar-";

/** Whether an artist's picture is an imported page's stand-in avatar. */
export function isPageAvatarImageUrl(value: string | null | undefined) {
  return (
    typeof value === "string" &&
    value.includes(`/images/validated/${PAGE_AVATAR_IMAGE_ID_PREFIX}`)
  );
}

export type LissenMediaImageSize = 128 | 256 | 512 | 1024;

/** Requests a supported image derivative while preserving non-Lissen URLs. */
export function toLissenMediaCdnImageUrl<T extends string | null | undefined>(
  value: T,
  size: LissenMediaImageSize,
): T {
  const cdnUrl = toLissenMediaCdnUrl(value);
  if (
    typeof cdnUrl !== "string" ||
    cdnUrl.includes("file:") ||
    !cdnUrl.includes("lissen.live")
  ) {
    return cdnUrl;
  }

  const baseUrl = cdnUrl.includes("_size=")
    ? cdnUrl.split("_size=").at(0)
    : cdnUrl;
  return `${baseUrl}_size=${size}` as T;
}

/**
 * Deterministic on-demand-publish directory prefix for one song's HLS
 * output, relative to the media bucket (`songs/<songId>` in
 * production; `<mediaPathPrefix>songs/<songId>` elsewhere). Single
 * source of truth shared across the deploy boundary between `apps/api`
 * (`on-demand-audio.ts` -- the `firePublishIntents` payload / `urlOnline`
 * wire field producer for `status: "unpublished"` songs) and
 * `apps/worker-publish` (`dest.ts` -- the actual transcode write target,
 * `songDestPrefix`) -- both already depend on `@libs/utils`, so this
 * replaces what used to be two hand-synced copies of the same convention.
 *
 * `prefix` is the environment's `MEDIA_PATH_PREFIX` (see
 * `deploymentContext.mediaPathPrefix` in `@libs/environment-contracts`) --
 * `""` in production, `"preview/pr-<n>/"` in previews. Kept a pure function
 * with an explicit param rather than reading env directly, so callers
 * control exactly which environment's prefix applies (and tests don't need
 * to fake env vars).
 */
export function onDemandAudioDirPrefix(songId: string, prefix = ""): string {
  return `${prefix}songs/${songId}`;
}

/**
 * Deterministic on-demand-publish object path for a song's HLS master
 * playlist (`<prefix>songs/<songId>/master.m3u8`). See
 * `onDemandAudioDirPrefix` for the shared-source-of-truth rationale and the
 * `prefix` param.
 */
export function onDemandAudioMasterObjectPath(
  songId: string,
  prefix = "",
): string {
  return `${onDemandAudioDirPrefix(songId, prefix)}/master.m3u8`;
}

/**
 * Deterministic path for a song's TRANSIENT master playlist
 * (`<prefix>songs/<songId>/master.tmp.m3u8`) — the two-generation publish's
 * gen-0 master, written the moment the song is playable and pointing at the
 * growing `.tmp.` rendition playlists. Distinct object from the final
 * `master.m3u8` (see `onDemandAudioMasterObjectPath`), which is written ONCE
 * at finalization and is immutable/never-swept: the tmp master carries the
 * `.tmp.` marker so the 1-day lifecycle rule reclaims it, and its no-cache
 * mutability never contaminates the durable final master's cacheability.
 *
 * The pair is what makes a 404 on the final master unambiguous ("not yet
 * finalized → (re)publish"): the final master's mere existence means the whole
 * song is done and its objects are immutable, so it can never 200-while-broken
 * the way a single rewritten-in-place master could.
 */
export function onDemandAudioMasterTmpObjectPath(
  songId: string,
  prefix = "",
): string {
  return `${onDemandAudioDirPrefix(songId, prefix)}/master.tmp.m3u8`;
}
